import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ChevronLeft, Cookie } from 'lucide-react';

const CookiePolicyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center">
        <div className="max-w-4xl mx-auto w-full flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" className="text-slate-600">
              <ChevronLeft className="w-5 h-5 mr-1" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="py-12 px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 lg:p-12 shadow-sm">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
                <Cookie className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Cookie Policy
                </h1>
                <p className="text-slate-500">Last updated: January 2025</p>
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">1. What Are Cookies?</h2>
                <p className="text-slate-600 mb-4">
                  Cookies are small text files that are stored on your device when you visit a website. They help websites remember your preferences and improve your browsing experience.
                </p>
                <p className="text-slate-600">
                  Career Step uses cookies and similar technologies to provide, protect, and improve our CV creation platform.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">2. Types of Cookies We Use</h2>
                
                <div className="bg-slate-50 rounded-xl p-5 mb-4">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">Essential Cookies</h3>
                  <p className="text-slate-600 mb-2">Required for the website to function properly.</p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1">
                    <li>Authentication tokens (to keep you logged in)</li>
                    <li>Session management</li>
                    <li>Security features</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-5 mb-4">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">Functional Cookies</h3>
                  <p className="text-slate-600 mb-2">Remember your preferences and settings.</p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1">
                    <li>Language preferences</li>
                    <li>Display settings</li>
                    <li>Form data (to save your progress)</li>
                  </ul>
                </div>

                <div className="bg-slate-50 rounded-xl p-5 mb-4">
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">Analytics Cookies</h3>
                  <p className="text-slate-600 mb-2">Help us understand how visitors use our site.</p>
                  <ul className="list-disc pl-6 text-slate-600 space-y-1">
                    <li>Pages visited and time spent</li>
                    <li>Features used</li>
                    <li>Error reports</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">3. Cookies We Use</h2>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="border border-slate-200 px-4 py-2 text-left text-slate-900">Cookie Name</th>
                        <th className="border border-slate-200 px-4 py-2 text-left text-slate-900">Purpose</th>
                        <th className="border border-slate-200 px-4 py-2 text-left text-slate-900">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2 text-slate-600">token</td>
                        <td className="border border-slate-200 px-4 py-2 text-slate-600">Authentication</td>
                        <td className="border border-slate-200 px-4 py-2 text-slate-600">24 hours</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2 text-slate-600">session_id</td>
                        <td className="border border-slate-200 px-4 py-2 text-slate-600">Session management</td>
                        <td className="border border-slate-200 px-4 py-2 text-slate-600">Session</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2 text-slate-600">preferences</td>
                        <td className="border border-slate-200 px-4 py-2 text-slate-600">User preferences</td>
                        <td className="border border-slate-200 px-4 py-2 text-slate-600">1 year</td>
                      </tr>
                      <tr>
                        <td className="border border-slate-200 px-4 py-2 text-slate-600">cookie_consent</td>
                        <td className="border border-slate-200 px-4 py-2 text-slate-600">Consent tracking</td>
                        <td className="border border-slate-200 px-4 py-2 text-slate-600">1 year</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">4. Third-Party Cookies</h2>
                <p className="text-slate-600 mb-4">
                  We may use third-party services that set their own cookies:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>PayFast:</strong> Payment processing and fraud prevention</li>
                  <li><strong>Analytics services:</strong> To understand site usage (anonymized data)</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">5. Managing Cookies</h2>
                <p className="text-slate-600 mb-4">
                  You can control cookies through your browser settings:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                  <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
                  <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies</li>
                  <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                  <li><strong>Edge:</strong> Settings → Cookies and Site Permissions</li>
                </ul>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-amber-800 text-sm">
                    <strong>Note:</strong> Disabling essential cookies may prevent you from using certain features of Career Step, such as staying logged in or saving your CV progress.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">6. Local Storage</h2>
                <p className="text-slate-600">
                  In addition to cookies, we use browser local storage to:
                </p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2 mt-2">
                  <li>Store your authentication token securely</li>
                  <li>Cache certain data for faster page loads</li>
                  <li>Save form progress to prevent data loss</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">7. Do Not Track</h2>
                <p className="text-slate-600">
                  Some browsers offer a "Do Not Track" feature. We respect this preference and limit tracking when this signal is detected, though essential cookies will still be used to provide our service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4">8. Updates to This Policy</h2>
                <p className="text-slate-600">
                  We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated revision date. Your continued use of Career Step after changes indicates acceptance of the updated policy.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-slate-900 mb-4">9. Contact Us</h2>
                <p className="text-slate-600 mb-4">
                  If you have questions about our use of cookies, please contact us:
                </p>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-slate-700"><strong>Career Step</strong></p>
                  <p className="text-slate-600">Email: support@careerstep.co.za</p>
                  <p className="text-slate-600">South Africa</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CookiePolicyPage;
